import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { testService } from '../services/api';
import { toast } from 'sonner';
import { Download, Star, ChevronDown } from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Legend, ResponsiveContainer, Tooltip
} from 'recharts';

export const RapportTestPage = () => {
  const { testId } = useParams();
  const [test, setTest] = useState(null);
  const [radarData, setRadarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testRes, radarRes] = await Promise.all([
          testService.getById(testId),
          testService.getRadarData(testId)
        ]);
        setTest(testRes.data);
        setRadarData(radarRes.data);
      } catch (error) {
        toast.error('Test non trouvé');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [testId]);

  const handleExportPDF = async (anonyme = false, format = 'complet') => {
    try {
      setExportMenuOpen(false);
      await testService.exportPdf(testId, anonyme, format);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  // Close export menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <Layout title="Rapport de test" breadcrumbs={['Accueil', 'Rapport']}>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Chargement...</p>
        </div>
      </Layout>
    );
  }

  if (!test) {
    return (
      <Layout title="Rapport de test" breadcrumbs={['Accueil', 'Rapport']}>
        <div className="text-center py-12">
          <p className="text-slate-400">Test non trouvé</p>
        </div>
      </Layout>
    );
  }

  const questions = test.questionnaire?.questions || [];
  const scores = test.scores || [];

  // Build tags-based strong/mastered/weak points
  const strongTags = [];
  const masteredTags = [];
  const weakTags = [];
  questions.forEach((q, i) => {
    const score = scores[i] || 0;
    const tags = (q.tags || []).slice(0, 2);
    const label = tags.length > 0 ? tags.join(' / ') : `Question ${i + 1}`;
    if (score > 7.5) strongTags.push(label);
    else if (score >= 5) masteredTags.push(label);
    else weakTags.push(label);
  });

  return (
    <Layout
      title={test.test_name}
      breadcrumbs={['Accueil', 'Historique', 'Rapport']}
    >
      <div className="mb-6">
        <div className="relative inline-block" ref={exportRef}>
          <button
            onClick={() => setExportMenuOpen(!exportMenuOpen)}
            className="eva-btn-primary flex items-center gap-2"
            data-testid="export-pdf-button"
          >
            <Download size={18} />
            Exporter en PDF
            <ChevronDown size={16} className={`transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {exportMenuOpen && (
            <div className="absolute left-0 top-full mt-1 bg-[#0B132B] border border-white/10 rounded-lg shadow-lg z-20 min-w-[260px]" data-testid="export-menu">
              <p className="px-4 pt-3 pb-1 text-xs text-slate-400 uppercase tracking-wider font-semibold">Version courte</p>
              <button
                onClick={() => handleExportPDF(false, 'court')}
                className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors"
                data-testid="export-court-nominatif"
              >
                <span className="font-medium">Nominatif — Court</span>
                <p className="text-xs text-slate-400 mt-0.5">Score + spider map uniquement</p>
              </button>
              <button
                onClick={() => handleExportPDF(true, 'court')}
                className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors border-t border-white/10"
                data-testid="export-court-anonyme"
              >
                <span className="font-medium">Anonyme — Court</span>
                <p className="text-xs text-slate-400 mt-0.5">Trigramme + spider map uniquement</p>
              </button>
              <p className="px-4 pt-3 pb-1 text-xs text-slate-400 uppercase tracking-wider font-semibold border-t border-white/10">Version complète</p>
              <button
                onClick={() => handleExportPDF(false, 'complet')}
                className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors"
                data-testid="export-complet-nominatif"
              >
                <span className="font-medium">Nominatif — Complet</span>
                <p className="text-xs text-slate-400 mt-0.5">Score + spider map + détail questions/réponses</p>
              </button>
              <button
                onClick={() => handleExportPDF(true, 'complet')}
                className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors rounded-b-lg border-t border-white/10"
                data-testid="export-complet-anonyme"
              >
                <span className="font-medium">Anonyme — Complet</span>
                <p className="text-xs text-slate-400 mt-0.5">Trigramme + spider map + détail questions/réponses</p>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Summary Card */}
      <div className="eva-card mb-6" data-testid="test-summary">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Candidat</p>
            <p className="text-lg font-semibold text-white">
              {test.prenom_candidat} {test.nom_candidat}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Date</p>
            <p className="text-base text-slate-300">
              {new Date(test.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Poste</p>
            <p className="text-base text-slate-300">
              {test.questionnaire?.fonction} - {test.questionnaire?.niveau}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Client</p>
            <p className="text-base text-slate-300">
              {test.questionnaire?.client || 'N/A'}
              {test.questionnaire?.ref_ao && ` — ${test.questionnaire.ref_ao}`}
            </p>
          </div>
        </div>
      </div>

      {/* Score + Evaluation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="eva-card" data-testid="score-card">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Score moyen</p>
          <div className="flex items-end gap-4 mb-4">
            <p className="text-5xl font-bold text-white">{test.average_score?.toFixed(1)}</p>
            <p className="text-2xl text-slate-400 mb-2">/10</p>
          </div>
          <div className="flex gap-1">
            {[...Array(10)].map((_, i) => (
              <Star
                key={i}
                size={20}
                fill={i < Math.round(test.average_score) ? '#F59E0B' : '#1E293B'}
                stroke={i < Math.round(test.average_score) ? '#F59E0B' : '#1E293B'}
              />
            ))}
          </div>
        </div>

        <div className="eva-card" data-testid="evaluation-card">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Evaluation</p>
          <div className={`inline-block px-4 py-2 rounded-lg font-semibold ${
            test.evaluation === 'Très bon niveau' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/20' :
            test.evaluation === 'Au-dessus de la moyenne' ? 'bg-[#10B981] bg-opacity-15 text-[#10B981]' :
            test.evaluation === 'Dans la moyenne' ? 'bg-[#F59E0B] bg-opacity-15 text-[#F59E0B]' :
            'bg-[#EF4444] bg-opacity-15 text-[#EF4444]'
          }`}>
            {test.evaluation}
          </div>
        </div>
      </div>

      {/* Spider/Radar Chart */}
      {radarData.length > 0 && (
        <div className="eva-card mb-6" data-testid="radar-chart">
          <h3 className="text-lg font-semibold text-white mb-4">Synthèse des compétences</h3>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
              <PolarGrid stroke="rgba(255,255,255,0.08)" />
              <PolarAngleAxis
                dataKey="label"
                tick={{ fill: '#495057', fontSize: 12 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 10]}
                tickCount={6}
                tick={{ fill: '#64748b', fontSize: 11 }}
              />
              <Radar
                name="Candidat"
                dataKey="candidat"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.25}
                strokeWidth={2}
                dot={{ r: 4, fill: '#3B82F6' }}
              />
              <Radar
                name="Moyenne"
                dataKey="moyenne"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.15}
                strokeWidth={2}
                dot={{ r: 4, fill: '#10B981' }}
              />
              <Legend
                wrapperStyle={{ fontSize: '14px', color: '#495057' }}
              />
              <Tooltip
                contentStyle={{
                  background: 'white',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                formatter={(value) => `${value}/10`}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Strong Points, Mastered Skills and Improvement Areas - using tags */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {strongTags.length > 0 && (
          <div className="eva-card" data-testid="strong-points">
            <h3 className="text-lg font-semibold text-white mb-4">Points forts</h3>
            <div className="flex flex-wrap gap-2">
              {strongTags.map((tag, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 text-[#10B981] rounded-full text-sm font-medium"
                >
                  <span className="text-base">+</span>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {masteredTags.length > 0 && (
          <div className="eva-card" data-testid="mastered-skills">
            <h3 className="text-lg font-semibold text-white mb-4">Compétences maîtrisées</h3>
            <div className="flex flex-wrap gap-2">
              {masteredTags.map((tag, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/15 text-[#3B82F6] rounded-full text-sm font-medium"
                >
                  <span className="text-base">✓</span>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {weakTags.length > 0 && (
          <div className="eva-card" data-testid="improvement-areas">
            <h3 className="text-lg font-semibold text-white mb-4">Axes d&apos;amélioration</h3>
            <div className="flex flex-wrap gap-2">
              {weakTags.map((tag, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/15 text-[#F59E0B] rounded-full text-sm font-medium"
                >
                  <span className="text-base">!</span>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Detailed Questions */}
      <div className="eva-card" data-testid="detailed-questions">
        <h3 className="text-lg font-semibold text-white mb-6">Détail par question</h3>
        <div className="space-y-6">
          {questions.map((question, i) => (
            <div key={i} className="border-b border-white/10 pb-6 last:border-0 last:pb-0">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-white">Question {i + 1}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#3B82F6]/10 text-[#3B82F6]">
                      QCM
                    </span>
                  </div>
                  {question.tags && question.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {question.tags.slice(0, 3).map((tag, j) => (
                        <span key={j} className="px-2 py-0.5 bg-white/5 text-xs text-slate-400 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  {[...Array(10)].map((_, j) => (
                    <Star
                      key={j}
                      size={14}
                      fill={j < (scores[i] || 0) ? '#F59E0B' : '#1E293B'}
                      stroke={j < (scores[i] || 0) ? '#F59E0B' : '#1E293B'}
                    />
                  ))}
                </div>
              </div>
              <p className="text-slate-300 mb-3">{question.text}</p>

              {/* QCM Choices */}
              {question.choices && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">
                    Choix de réponse
                  </p>
                  <ul className="space-y-1">
                    {question.choices.map((choice, j) => {
                      const selectedChoicesForQ = test.selected_choices_per_question?.[i.toString()] || [];
                      const isSelected = selectedChoicesForQ.includes(j);
                      const isCorrect = choice.is_correct;
                      let colorClass = 'text-slate-300';
                      let icon = '○';
                      if (isSelected && isCorrect) { colorClass = 'text-[#10B981] font-medium'; icon = '✓'; }
                      else if (isSelected && !isCorrect) { colorClass = 'text-[#EF4444] font-medium'; icon = '✗'; }
                      else if (!isSelected && isCorrect) { colorClass = 'text-[#F59E0B]'; icon = '→'; }
                      return (
                        <li key={j} className={`text-sm flex items-start gap-2 ${colorClass}`}>
                          <span className="mt-0.5">{icon}</span>
                          {choice.text}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};
